import React, { Fragment } from "react";
import { useFormikContext } from "formik";
import TemplateProgressControls from "components/TemplateBuilderForm/TemplateProgressControls";
import DetailLoader from "components/DetailLoader";
import { withRouter } from "next/router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getResourceAppMerchantData } from "utils/mapStateToProps";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { WHITE_TAB_STYLES } from "components/Tabs";
import Tabs from "components/Tabs";
import ProductSearchList from "components/ProductSearchList";
import SlimCTA from "components/SlimCTA";
import ProductTemplatePickerList from "./ProductTemplatePickerList";
import NewProductsForm from "./NewProductsForm";
import { PRICING_TABLE_PRODUCT_LIMIT_MAX } from "components/FormerEditor/common/constants";
import Pill from "components/Pill";
import Padder from "components/Padder";
import { toggleProductSelect } from "utils/pricing";
import {
  TEMPLATE_PRODUCT_PICKER_TABS,
  TEMPLATE_PRODUCT_TEMPLATES_TAB,
  TEMPLATE_NEW_PRODUCT_TAB,
  TEMPLATE_PRODUCTS_TAB
} from "utils/constants/template";
import get from "lodash/get";
import { ensureProductsPricesCouponsPromotionCodes } from "utils/hooks";
import { TEMPLATE_PAGE_BUILDER } from "utils/analytics";
import { TEMPLATE_PAGE_TYPE } from "components/FormerEditor/common/constants/entity";
import { getNonFlexProducts, reduceRecurringProducts } from "utils/product";
import { useTemplateContinue } from "utils/formContext";
import { formFactorAction } from "utils/view";

const TemplateProductPicker = ({ tracking, router, actions }) => {
  const { values, setFieldValue } = useFormikContext();

  const {
    products: selectedProducts,
    form: { context, template }
  } = values;
  const isPricingTemplate = template === TEMPLATE_PAGE_TYPE.PRICING;
  const CONTEXT_TABS_SET = get(values, "form.products.tabs", []);

  const { nextAction } = useTemplateContinue();

  const activeProducts = selectedProducts.map(({ id }) => id);
  const maxProductsReached =
    isPricingTemplate &&
    activeProducts.length >= PRICING_TABLE_PRODUCT_LIMIT_MAX;

  const {
    loadingProductsPrices,
    loadingCouponsPromotionCodes,
    products: allProducts,
    prices: allPrices
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });

  /**
   * The pricing page is currently restricted to recurring priced products
   */
  const products = isPricingTemplate
    ? reduceRecurringProducts({ products: allProducts, prices: allPrices })
    : getNonFlexProducts(allProducts);

  const loading = Boolean(
    loadingProductsPrices || loadingCouponsPromotionCodes
  );

  const productsExist = Boolean(products.length > 0);

  const tabProps = {
    minWidth: false,
    equalWidth: false,
    pathPrefix: "",
    customStyles: WHITE_TAB_STYLES,
    customClasses: {
      tabs: {
        list:
          "list ma0 pa0 nowrap center overflow-x-auto absolute top-0 left-0 right-0 ph3"
      }
    },
    // Reset product choices on tab change
    onUpdate: () => {
      setFieldValue("products", []);
    }
  };

  const tabs = [];

  if (TEMPLATE_PRODUCT_TEMPLATES_TAB[template]) {
    tabs.push({
      name: "Templates",
      id: TEMPLATE_PRODUCT_PICKER_TABS.TEMPLATE,
      node: (
        <Fragment>
          <ProductTemplatePickerList />
          <Padder theme="visible" />
        </Fragment>
      )
    });
  }
  if (TEMPLATE_NEW_PRODUCT_TAB[template]) {
    tabs.push({
      name: "New",
      id: TEMPLATE_PRODUCT_PICKER_TABS.NEW,
      node: (
        <div className="pt3">
          <NewProductsForm />
          <Padder theme="visible" />
        </div>
      )
    });
  }

  if (TEMPLATE_PRODUCTS_TAB[template] && productsExist) {
    tabs.unshift({
      name: "Products",
      id: TEMPLATE_PRODUCT_PICKER_TABS.PRODUCT,
      node: (
        <div className="ph3 pt3">
          {maxProductsReached && (
            <Pill
              copy={`Max products per pricing page is ${PRICING_TABLE_PRODUCT_LIMIT_MAX}`}
            />
          )}
          <ProductSearchList
            customClasses={{
              container: "ba hairline-1 br2 h-100"
            }}
            products={products}
            prices={allPrices}
            activeProducts={activeProducts}
            setActiveProduct={toggleProductSelect({
              products,
              prices: allPrices,
              maxProductsReached,
              selectedProducts,
              setFieldValue
            })}
            clearSelections={() => {
              setFieldValue("products", []);
            }}
          />
          <Padder theme="visible" />
        </div>
      )
    });
  }

  /**
   * Filter tabs components by the builder context (i.e. page / link)
   */
  tabProps.tabs = tabs.filter(({ id }) => CONTEXT_TABS_SET.indexOf(id) > -1);
  let primary;
  let secondary;
  if (productsExist) {
    primary = `Pick your products`;
    secondary = `Pick the products you want to sell on your page.`;
  } else if (isPricingTemplate) {
    primary = `Create some products`;
    secondary = `Create the products you want to sell on your page.`;
  } else {
    primary = `Create your page`;
    secondary = `Complete this step and head to the editor where you can create some products.`;
  }

  let tertiary;
  if (context === TEMPLATE_PAGE_BUILDER && isPricingTemplate) {
    if (productsExist) {
      tertiary =
        "You can also create new products using one of our templates or start from scratch.";
    } else {
      tertiary =
        "You can use one of our pricing templates to get started of create your own from scratch.";
    }
  }

  const copyContent = loading ? (
    <div className="w-100 w-70-ns">
      <div className="h3 shimmer w-50" />
      <div className="h2 shimmer w-75 mt3" />
      <div className="h2 shimmer w-80 mt2" />
      <div className="h2 shimmer w-60 mt2" />
      <div className="h2 shimmer w-50 mt2" />
    </div>
  ) : (
    <div className="w-100 w-70-ns">
      <div className="f3 f2-ns fw5 pt3">{primary}</div>
      <div className="f5 f3-ns pt3 lh-copy">{secondary}</div>
      {tertiary && <div className="f6 f4-ns pt2 lh-copy">{tertiary}</div>}
    </div>
  );

  /**
   * Add the context of the active tab to the tracking event
   * - product
   * - template
   * - new
   */
  const tabRoot = router.query.tab && router.query.tab.split(".")[0];
  const extendedTracking = tabRoot
    ? {
        ...tracking,
        tab: tabRoot
      }
    : { ...tracking };

  return (
    <div className="w-100 flex flex-column flex-row-ns">
      <div className="dn flex-ns flex-column w-100 w-50-ns pt3 pl3 pt3 pa3-ns pa4-ns bg-content--secondary">
        {copyContent}
      </div>
      <div className="flex flex-column w-100 w-50-ns relative">
        <div
          style={{ top: 56 }}
          className="sticky top-0 pb3 bg-white bb hairline-1 z-6"
        >
          <TemplateProgressControls tracking={extendedTracking} />
        </div>
        {loading ? (
          <DetailLoader />
        ) : (
          <Fragment>
            <div className="flex dn-ns bg-white flex-column w-100 w-50-ns pt2 pl3 pb3">
              {copyContent}
            </div>
            <div className="pt3 bg-white w-100">
              {tabProps.tabs.length > 0 ? (
                <Tabs {...tabProps} />
              ) : (
                <div className="pa3">
                  <SlimCTA
                    header="No products"
                    subheader={`There are no products in your account to select from. Not to worry, ${formFactorAction().toLowerCase()} next and you can create some later.`}
                    actions={[
                      {
                        copy: "Next",
                        onClick: nextAction
                      }
                    ]}
                  />
                </div>
              )}
              <Padder />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (
  state,
  { router, application, resource, resourceUUID }
) => {
  const ctxResourceData = getResourceAppMerchantData(state, {
    router,
    application,
    resource,
    resourceUUID
  });

  const base = {
    ...ctxResourceData
  };

  return base;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes
    },
    dispatch
  )
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TemplateProductPicker)
);
